import axios from '../../axios';
import type { AiJobEnvelopeType, AiJobStatusType, AiRequestType } from '@econtractor/types';

export type AiJobStatus = AiJobStatusType;
export type AiJobEnvelope<TResult = unknown> = AiJobEnvelopeType<TResult>;

export interface RunAiJobOptions<TPayload, TResult> {
    requestType: AiRequestType;
    payload: TPayload;
    pollIntervalMs?: number;
    timeoutMs?: number;
    onPoll?: (job: AiJobEnvelope<TResult>) => void;
    signal?: AbortSignal;
}

const sleep = (ms: number): Promise<void> =>
    new Promise((resolve) => {
        setTimeout(resolve, ms);
    });

const parseServerError = (error: unknown, fallback: string): string => {
    if (error && typeof error === 'object') {
        if ('error' in error && typeof (error as { error?: string }).error === 'string') {
            return (error as { error: string }).error;
        }
        if ('message' in error && typeof (error as { message?: string }).message === 'string') {
            return (error as { message: string }).message;
        }
    }
    return fallback;
};

const ensureNotAborted = (signal?: AbortSignal): void => {
    if (signal?.aborted) {
        throw new Error('ai_job_aborted');
    }
};

const createJob = async <TPayload, TResult>(
    requestType: string,
    payload: TPayload,
    signal?: AbortSignal,
): Promise<AiJobEnvelope<TResult>> => {
    ensureNotAborted(signal);
    const response = await axios.post('/ai/job', {
        mode: 'start',
        requestType,
        payload,
    }, {
        signal,
    });
    if (!response?.data?.stat || !response?.data?.job?.id) {
        throw new Error(parseServerError(response?.data, 'ai_job_create_failed'));
    }
    return response.data.job as AiJobEnvelope<TResult>;
};

const getJobStatus = async <TResult>(jobId: string, signal?: AbortSignal): Promise<AiJobEnvelope<TResult>> => {
    ensureNotAborted(signal);
    const response = await axios.post('/ai/job', {
        mode: 'status',
        jobId,
    }, {
        signal,
    });
    if (!response?.data?.stat || !response?.data?.job?.id) {
        throw new Error(parseServerError(response?.data, 'ai_job_status_failed'));
    }
    return response.data.job as AiJobEnvelope<TResult>;
};

export const runAiJobWithPolling = async <TPayload, TResult>({
    requestType,
    payload,
    pollIntervalMs = 2000,
    timeoutMs = 120000,
    onPoll,
    signal,
}: RunAiJobOptions<TPayload, TResult>): Promise<AiJobEnvelope<TResult>> => {
    ensureNotAborted(signal);
    const startedAt = Date.now();
    const startedJob = await createJob<TPayload, TResult>(requestType, payload, signal);
    let currentJob = startedJob;
    onPoll?.(currentJob);

    while (currentJob.status === 'pending' || currentJob.status === 'processing') {
        ensureNotAborted(signal);
        if (Date.now() - startedAt > timeoutMs) {
            throw new Error('ai_job_timeout');
        }
        await sleep(pollIntervalMs);
        currentJob = await getJobStatus<TResult>(currentJob.id, signal);
        onPoll?.(currentJob);
    }

    if (currentJob.status === 'failed') {
        throw new Error(currentJob.error || 'ai_job_failed');
    }

    return currentJob;
};
